/**
 * Invite form for the Admin Console (DEV-3767).
 *
 * The invitation always lands in the active company: the console manages one
 * tenant at a time, and the company the nav picker holds is the one the invite
 * acts on. Roles offered are the assignable ones — `Cyngn-Vehicle` is a
 * machine principal and is never assignable from the console.
 */
import React, { FC, memo, useState } from "react";

import {
  ASSIGNABLE_ROLES,
  AssignableRole,
  InviteUserPayload,
} from "Utils/adminUsersAPI";

import {
  ActionButtonStyled,
  InviteButtonStyled,
  InviteFieldStyled,
  InviteFormStyled,
  SelectStyled,
} from "./styles";

type InviteUserFormProps = {
  activeCompanyId: string;
  activeCompanyName: string;
  onInvite: (payload: InviteUserPayload) => Promise<boolean>;
  onClose: () => void;
};

export const InviteUserForm: FC<InviteUserFormProps> = memo(
  ({ activeCompanyId, activeCompanyName, onInvite, onClose }) => {
    const [email, setEmail] = useState<string>("");
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [role, setRole] = useState<AssignableRole>("Cyngn-Viewer");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const handleSubmit = async (event: React.FormEvent) => {
      event.preventDefault();
      if (!email || isSubmitting) return;

      setIsSubmitting(true);
      const invited = await onInvite({
        email,
        company: activeCompanyId,
        role,
        firstName,
        lastName,
      });
      setIsSubmitting(false);
      if (invited) {
        onClose();
      }
    };

    return (
      <InviteFormStyled data-test-id="invite_user_form" onSubmit={handleSubmit}>
        <InviteFieldStyled>
          Email
          <input
            data-test-id="invite_email"
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </InviteFieldStyled>
        <InviteFieldStyled>
          First name
          <input
            data-test-id="invite_first_name"
            value={firstName}
            maxLength={25}
            onChange={(event) => setFirstName(event.target.value)}
          />
        </InviteFieldStyled>
        <InviteFieldStyled>
          Last name
          <input
            data-test-id="invite_last_name"
            value={lastName}
            maxLength={25}
            onChange={(event) => setLastName(event.target.value)}
          />
        </InviteFieldStyled>
        <InviteFieldStyled>
          Role
          <SelectStyled
            data-test-id="invite_role"
            value={role}
            onChange={(event) => setRole(event.target.value as AssignableRole)}
          >
            {ASSIGNABLE_ROLES.map((assignableRole) => (
              <option key={assignableRole} value={assignableRole}>
                {assignableRole}
              </option>
            ))}
          </SelectStyled>
        </InviteFieldStyled>
        <InviteFieldStyled>
          Company
          <input
            data-test-id="invite_company"
            value={activeCompanyName}
            disabled
            readOnly
          />
        </InviteFieldStyled>
        <InviteButtonStyled
          data-test-id="invite_submit"
          type="submit"
          disabled={isSubmitting}
        >
          Send invite
        </InviteButtonStyled>
        <ActionButtonStyled
          data-test-id="invite_cancel"
          type="button"
          onClick={onClose}
        >
          Cancel
        </ActionButtonStyled>
      </InviteFormStyled>
    );
  }
);
